import { debounce } from 'lodash-es';
import { getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useBreakpoint } from './useBreakpoint';

const RESIZE_DEBOUNCE_MS = 200;
const SELECTOR = '.set-min-height';

// Equalizes the heights of elements marked with `.set-min-height` across `columns`
// same-shaped columns: the i-th marked element of every column gets the max height
// of the i-th elements. `getColumns` receives the component proxy, so Options API
// consumers can derive the count from options-defined computeds.
export function useMinHeight(getColumns: (vm?: any) => number | undefined) {
    const { proxy } = getCurrentInstance();
    const { isDesktop } = useBreakpoint();
    const maxHeights = ref<string[]>([]);

    const setMinHeight = async () => {
        const el = proxy?.$el as HTMLElement | null;
        if (!isDesktop.value || !el?.querySelectorAll) {
            if (maxHeights.value.length) maxHeights.value = [];
            return;
        }

        maxHeights.value = [];

        await nextTick();

        const columns = getColumns(proxy) || 3;
        const heights = Array.from(el.querySelectorAll(SELECTOR), (item) => (item as HTMLElement).offsetHeight);
        const elementsPerColumn = heights.length / columns;

        maxHeights.value = Array.from({ length: elementsPerColumn }, (_, element) => {
            const columnHeights = Array.from({ length: columns }, (__, column) => heights[column * elementsPerColumn + element]);
            return `${Math.max(...columnHeights)}px`;
        });
    };

    const setMinHeightDebounced = debounce(setMinHeight, RESIZE_DEBOUNCE_MS);

    const styleMinHeight = (index: number) => ({ 'min-height': maxHeights.value[index] });

    onMounted(() => {
        // Registered on mount so that `getColumns` can safely read options-defined
        // computeds, which do not exist yet while `setup` is running.
        watch(() => getColumns(proxy), () => setMinHeight());
        window.addEventListener('resize', setMinHeightDebounced);
        setMinHeight();
    });

    onBeforeUnmount(() => {
        setMinHeightDebounced.cancel();
        window.removeEventListener('resize', setMinHeightDebounced);
    });

    return { maxHeights, setMinHeight, styleMinHeight };
}
